/**
 * #516 — Marketing KPIs for the /admin dashboard: revenue by source, win rate,
 * CAC and CPA. Pure: no React, no Supabase. Quotes come from useKpiQuotes and
 * spend from the ad-spend-monthly edge function (live Meta + Google).
 *
 * Definitions (auto-selected defaults, flagged in the PR):
 * - Lead: a quote created in the month, excluding Square-synced rows.
 * - Won: not lost and in a sold stage (the same `soldStages` as useSalesStats).
 * - Win rate: won ÷ (won + lost); open quotes are left out.
 * - Revenue: sum of won quotes' totals, by the quote's created month.
 * - New customer: a customer whose first-ever won quote was created in the month.
 * - CAC = spend ÷ new customers. CPA = spend ÷ leads.
 */
import { format, parseISO, startOfMonth, subMonths } from "date-fns";
import type { AdSpendResponse } from "../../supabase/functions/ad-spend-monthly/spend.ts";

export interface KpiQuote {
  id: string;
  created_at: string;
  total: number | null;
  pipeline_stage: string | null;
  is_lost: boolean;
  contact_id: string | null;
  client_email: string | null;
  utm_source: string | null;
  gclid: string | null;
  fbclid: string | null;
  source: string | null;
  square_source: boolean | null;
}

export type KpiPeriod = "this_month" | "last_3_months" | "last_12_months";

const PERIOD_LENGTH: Record<KpiPeriod, number> = { this_month: 1, last_3_months: 3, last_12_months: 12 };
const WON_STAGES = ["deposit_pending", "booked", "won"];
const SOURCE_LABELS: Record<string, string> = {
  online_order: "Online order (no UTM)",
  marketing_cta: "Website form (no UTM)",
  plan_wizard: "Plan wizard (no UTM)",
  manual: "Manual / direct",
  import: "Imported",
};
export const QUOTE_CURRENCY = "CAD";

/** `count` months ending with `now`'s month, oldest first, as `yyyy-MM` (local time). */
function lastMonths(now: Date, count: number): string[] {
  const out: string[] = [];
  for (let i = count - 1; i >= 0; i--) out.push(format(startOfMonth(subMonths(now, i)), "yyyy-MM"));
  return out;
}

export function periodMonths(period: KpiPeriod, now: Date): string[] {
  return lastMonths(now, PERIOD_LENGTH[period]);
}

function monthOf(createdAt: string): string {
  return format(parseISO(createdAt), "yyyy-MM");
}

/** Square POS orders and Square-issued invoices aren't marketing leads. */
function isMarketingQuote(q: KpiQuote): boolean {
  return q.square_source !== true;
}

function isWon(q: KpiQuote): boolean {
  return !q.is_lost && WON_STAGES.includes(q.pipeline_stage ?? "");
}

function isLost(q: KpiQuote): boolean {
  return q.is_lost === true;
}

export function sourceLabel(q: KpiQuote): string {
  const utm = q.utm_source?.trim().toLowerCase();
  if (utm) return utm;
  if (q.gclid?.trim()) return "google";
  if (q.fbclid?.trim()) return "facebook";
  return SOURCE_LABELS[q.source ?? ""] ?? "Unknown";
}

function normalizeEmail(email: string | null): string | null {
  const e = email?.trim().toLowerCase();
  return e ? e : null;
}

function byCreated(a: KpiQuote, b: KpiQuote): number {
  return a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : a.id < b.id ? -1 : 1;
}

/** Normalized email → contact id, from quotes carrying both (earliest wins). */
function buildEmailToContact(quotes: KpiQuote[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const q of [...quotes].sort(byCreated)) {
    const email = normalizeEmail(q.client_email);
    if (email && q.contact_id && !map.has(email)) map.set(email, q.contact_id);
  }
  return map;
}

function customerKey(q: KpiQuote, emailToContact: Map<string, string>): string {
  if (q.contact_id) return q.contact_id;
  const email = normalizeEmail(q.client_email);
  if (email) return emailToContact.get(email) ?? `email:${email}`;
  return `quote:${q.id}`;
}

/** Customer key → `yyyy-MM` of that customer's first won quote. */
export function firstWonMonthByCustomer(quotes: KpiQuote[]): Map<string, string> {
  const marketing = quotes.filter(isMarketingQuote);
  const emailToContact = buildEmailToContact(marketing);
  const first = new Map<string, string>();
  for (const q of marketing.filter(isWon).sort(byCreated)) {
    const key = customerKey(q, emailToContact);
    if (!first.has(key)) first.set(key, monthOf(q.created_at));
  }
  return first;
}

function ratio(n: number, d: number): number | null {
  return d > 0 ? n / d : null;
}

interface Tally {
  leads: number;
  won: number;
  lost: number;
  revenue: number;
}

function tallyQuotes(quotes: KpiQuote[]): Tally {
  const t: Tally = { leads: 0, won: 0, lost: 0, revenue: 0 };
  for (const q of quotes) {
    t.leads += 1;
    if (isWon(q)) {
      t.won += 1;
      t.revenue += Number(q.total ?? 0);
    } else if (isLost(q)) {
      t.lost += 1;
    }
  }
  return t;
}

export interface SourceRow {
  source: string;
  leads: number;
  won: number;
  decided: number;
  winRate: number | null;
  revenue: number;
}

function rollupBySource(quotes: KpiQuote[]): SourceRow[] {
  const groups = new Map<string, KpiQuote[]>();
  for (const q of quotes) {
    const key = sourceLabel(q);
    groups.set(key, [...(groups.get(key) ?? []), q]);
  }
  const rows = [...groups.entries()].map(([source, qs]) => {
    const t = tallyQuotes(qs);
    const decided = t.won + t.lost;
    return { source, leads: t.leads, won: t.won, decided, winRate: ratio(t.won, decided), revenue: t.revenue };
  });
  return rows.sort((a, b) => b.revenue - a.revenue || b.leads - a.leads || a.source.localeCompare(b.source));
}

export interface KpiSummary {
  leads: number;
  won: number;
  lost: number;
  decided: number;
  winRate: number | null;
  revenue: number;
  newCustomers: number;
  spend: number | null;
  cac: number | null;
  cpa: number | null;
  bySource: SourceRow[];
}

// A month the server didn't cover (a tab left open past the 1st) is unknown,
// not $0, so it can't produce a real-looking $0.00 CAC.
function sumSpend(spend: Record<string, number> | null, months: string[]): number | null {
  if (!spend || months.some((m) => !(m in spend))) return null;
  return months.reduce((sum, m) => sum + spend[m], 0);
}

/**
 * Quotes in `months` (by created month) feed leads, won, lost and revenue.
 * New customers need every quote, since a first win can predate the period.
 * A month missing from `spend` makes spend unknown (null), not 0.
 */
export function computeKpis(
  quotes: KpiQuote[],
  spend: Record<string, number> | null,
  months: string[],
  firstWon: Map<string, string> = firstWonMonthByCustomer(quotes),
): KpiSummary {
  const inRange = new Set(months);
  const scoped = quotes.filter((q) => isMarketingQuote(q) && inRange.has(monthOf(q.created_at)));
  const t = tallyQuotes(scoped);
  const decided = t.won + t.lost;
  const newCustomers = [...firstWon.values()].filter((m) => inRange.has(m)).length;
  const total = sumSpend(spend, months);
  return {
    ...t,
    decided,
    winRate: ratio(t.won, decided),
    newCustomers,
    spend: total,
    cac: total === null ? null : ratio(total, newCustomers),
    cpa: total === null ? null : ratio(total, t.leads),
    bySource: rollupBySource(scoped),
  };
}

export interface SpendView {
  /** Meta + Google per month; null when either is unusable (see `reason`). */
  byMonth: Record<string, number> | null;
  meta: Record<string, number> | null;
  google: Record<string, number> | null;
  metaCurrency: string | null;
  googleCurrency: string | null;
  reason: string | null;
}

const EMPTY_VIEW: Omit<SpendView, "reason"> = {
  byMonth: null,
  meta: null,
  google: null,
  metaCurrency: null,
  googleCurrency: null,
};

function foreignCurrency(currency: string | null): boolean {
  return currency !== null && currency !== QUOTE_CURRENCY;
}

function spendReasons(res: AdSpendResponse): string[] {
  const reasons: string[] = [];
  if (!res.meta.ok) reasons.push(`Couldn't load Meta spend: ${res.meta.error}.`);
  if (!res.google.ok) reasons.push(`Couldn't load Google spend: ${res.google.error}.`);
  for (const [name, p] of [["Meta", res.meta], ["Google", res.google]] as const) {
    if (p.ok && foreignCurrency(p.currency)) {
      reasons.push(`${name} ad spend is in ${p.currency}; quotes are in ${QUOTE_CURRENCY}.`);
    }
  }
  return reasons;
}

/** Turns the edge function's response (or its fetch error) into what the KPIs use. */
export function combineSpend(res: AdSpendResponse | undefined, error: string | null): SpendView {
  if (error) return { ...EMPTY_VIEW, reason: `Couldn't load ad spend: ${error}` };
  if (!res) return { ...EMPTY_VIEW, reason: null };
  const meta = res.meta.ok ? res.meta.byMonth : null;
  const google = res.google.ok ? res.google.byMonth : null;
  const metaCurrency = res.meta.ok ? res.meta.currency : null;
  const googleCurrency = res.google.ok ? res.google.currency : null;
  const reasons = spendReasons(res);
  let byMonth: Record<string, number> | null = null;
  if (reasons.length === 0 && meta && google) {
    byMonth = {};
    for (const m of res.months) byMonth[m] = (meta[m] ?? 0) + (google[m] ?? 0);
  }
  return { byMonth, meta, google, metaCurrency, googleCurrency, reason: reasons.length ? reasons.join(" ") : null };
}

export type TrendRow = Omit<KpiSummary, "bySource"> & {
  month: string;
  label: string;
  metaSpend: number | null;
  googleSpend: number | null;
};

/** One row per month, oldest first, for the 12 months ending with `now`. */
export function monthlyTrend(quotes: KpiQuote[], spendView: SpendView, now: Date): TrendRow[] {
  const firstWon = firstWonMonthByCustomer(quotes);
  return lastMonths(now, 12).map((month) => {
    const { bySource: _bySource, ...summary } = computeKpis(quotes, spendView.byMonth, [month], firstWon);
    return {
      ...summary,
      month,
      label: format(parseISO(`${month}-01`), "MMM yyyy"),
      metaSpend: spendView.meta ? spendView.meta[month] ?? 0 : null,
      googleSpend: spendView.google ? spendView.google[month] ?? 0 : null,
    };
  });
}
